import axios from "axios";
import {
  setRecordingActive,
  isRecordingActive,
  decide401Action,
  decodeJwtPayload,
  tokenSecondsRemaining,
  isAuthFailure,
} from "./authPolicy";
import { track } from "./analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Phase 5 auth hardening — the ACCESS token lives in JS memory ONLY (never
// localStorage). Closing the tab discards it; there is no week-long bearer to
// steal. The long-lived credential is the httpOnly refresh cookie (hiretape_rt),
// which JS cannot read. /auth/refresh mints a fresh access token from it.
const LEGACY_TOKEN_KEY = "hireproof_token"; // pre-Phase-5 localStorage key (purged)
const CSRF_COOKIE_NAME = "hiretape_csrf";

let _accessToken = null;

export function getAccessToken() {
  return _accessToken;
}
export function setAccessToken(token) {
  _accessToken = typeof token === "string" && token.length > 0 ? token : null;
}
export function clearAccessToken() {
  _accessToken = null;
}

// Read the (non-httpOnly) double-submit CSRF token the backend set alongside
// the refresh cookie, to echo it back in the X-CSRF-Token header.
function readCsrfCookie() {
  const m = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + CSRF_COOKIE_NAME + "=([^;]+)")
  );
  return m ? decodeURIComponent(m[1]) : null;
}

// These paths are auth endpoints themselves — a 401 from them is meaningful to
// the caller (wrong password, dead session) and must NOT trigger a global
// redirect or an auto-refresh-retry loop.
const AUTH_PATHS = ["/auth/login", "/auth/signup", "/auth/me", "/auth/session", "/auth/logout", "/auth/refresh"];

function isAuthEndpoint(url = "") {
  return AUTH_PATHS.some((p) => url.endsWith(p) || url.includes(p + "?"));
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // send the httpOnly refresh cookie on /auth/* calls
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Attach the double-submit CSRF header (harmless for bearer-only business
  // endpoints; required by the cookie-authenticated /auth/logout).
  const csrf = readCsrfCookie();
  if (csrf) {
    config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

// ---- Single-flight refresh -------------------------------------------------
// CRITICAL (race-safety guardrail): the proactive recording-refresh timer and a
// reactive refresh-on-401 must NEVER fire two concurrent /auth/refresh calls —
// the second would replay an already-rotated token and (without the backend
// grace window) trip reuse-detection, revoking the family and logging out a
// recruiter mid-interview. A shared in-flight promise collapses all concurrent
// callers into ONE request.
let _refreshPromise = null;

function _sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function _doRefresh(attempt = 0) {
  const csrf = readCsrfCookie();
  const headers = { "X-Hiretape-Refresh": "1" };
  if (csrf) headers["X-CSRF-Token"] = csrf;
  try {
    const resp = await axios.post(`${API_BASE}/auth/refresh`, {}, {
      withCredentials: true,
      headers,
    });
    const newToken = resp?.data?.access_token;
    if (newToken && typeof newToken === "string") {
      setAccessToken(newToken);
      return true;
    }
    return false;
  } catch (err) {
    const code = err?.response?.data?.detail?.code;
    if (code === "refresh_raced" && attempt < 2) {
      // A concurrent refresh just rotated the cookie — retry once with the
      // now-current cookie. Family is intact; this is benign.
      await _sleep(300 * (attempt + 1));
      return _doRefresh(attempt + 1);
    }
    // refresh_reuse / refresh_invalid / expired / network → session is dead.
    clearAccessToken();
    return false;
  }
}

// Returns true if a fresh access token is now in memory, false otherwise.
export function refreshAccessToken() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      return await _doRefresh();
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

// Called on app load: purge any legacy localStorage bearer, then mint an access
// token from the refresh cookie (if a session exists).
export async function bootstrapAuth() {
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    /* ignore */
  }
  return refreshAccessToken();
}

// ---- Session invalidation --------------------------------------------------
let invalidating = false;
function invalidateSession(reason = "session_expired") {
  if (invalidating) return;
  invalidating = true;
  clearAccessToken();
  try {
    window.dispatchEvent(new CustomEvent("hireproof:auth-invalidated", { detail: { reason } }));
  } catch {
    /* CustomEvent unavailable in very old browsers — proceed anyway. */
  }
  const path = window.location.pathname;
  const publicRoutes = ["/login", "/signup", "/auth/callback", "/forbidden"];
  const isPublic =
    publicRoutes.some((p) => path === p || path.startsWith(p + "/")) || path.startsWith("/invite/");
  if (!isPublic) {
    window.location.assign(`/login?reason=${encodeURIComponent(reason)}`);
  } else {
    invalidating = false;
  }
}

// Refresh-and-retry a request that 401'd. Mints a fresh access token (single-
// flight) then replays the request up to `maxAttempts` times. Each replay is
// tagged `_authRetried` so the interceptor propagates its failures (no
// recursion). Returns the successful response, or null when exhausted.
async function refreshAndRetryRequest(config, maxAttempts) {
  const ok = await refreshAccessToken();
  if (!ok) return null;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const retryConfig = { ...config, _authRetried: true };
    if (retryConfig.headers) {
      // Force the request interceptor to attach the freshly-refreshed token.
      delete retryConfig.headers.Authorization;
      delete retryConfig.headers.authorization;
    }
    try {
      return await api.request(retryConfig);
    } catch {
      if (attempt < maxAttempts) {
        await _sleep(250 * attempt);
        await refreshAccessToken(); // re-mint in case it expired again
        continue;
      }
      return null;
    }
  }
  return null;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    // Phase 5a paywall — surface upgrade prompts globally (recording/interview
    // flows stay untouched; a mounted UpgradeModal listens for this event).
    if (status === 402) {
      const detail = error.response?.data?.detail;
      if (detail?.code === "upgrade_required") {
        track("paywall_hit", {});
        try {
          window.dispatchEvent(new CustomEvent("hiretape:upgrade-required", { detail }));
        } catch {
          /* CustomEvent unavailable — proceed */
        }
      }
      return Promise.reject(error);
    }
    if (status !== 401) return Promise.reject(error);

    const config = error.config || {};
    const action = decide401Action({
      isAuthUrl: isAuthEndpoint(config.url || ""),
      recordingActive: isRecordingActive(),
      alreadyRetried: Boolean(config._authRetried),
    });

    if (action === "propagate") return Promise.reject(error);

    if (action === "retry-then-defer") {
      // A recording is in progress — NEVER hard-logout (that would destroy the
      // in-flight interview). Try hard to save: finalize sets _authMaxRetries=3.
      const resp = await refreshAndRetryRequest(config, config._authMaxRetries ?? 2);
      if (resp) return resp;
      // Recovery exhausted mid-recording: defer sign-out. The recording flow
      // surfaces an honest "stored on this device — re-sign in to recover"
      // message and keeps the page (and IndexedDB chunks) alive.
      return Promise.reject(error);
    }

    // Normal path: refresh+retry tolerates an expired access token (refresh
    // cookie still valid) before we conclude the session is genuinely dead.
    const resp = await refreshAndRetryRequest(config, config._authMaxRetries ?? 1);
    if (resp) return resp;
    invalidateSession("session_expired");
    return Promise.reject(error);
  }
);

export { invalidateSession };
export {
  setRecordingActive,
  isRecordingActive,
  tokenSecondsRemaining,
  isAuthFailure,
  decodeJwtPayload,
};

export default api;
